import { Button, Tab, TabList } from '@fluentui/react-components';
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { GraderCodeBundleAsset } from '../../../services/grader.interface.js';
import { useUserBi } from '../../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionScenario,
  ActionScenarioType,
  PanelRegion,
  PanelType,
} from '../../../telemetry/userbi.js';
import { useStyles } from '../GraderPage.style.js';

import { HighlightedSource } from './HighlightedSource.js';
import { display, label } from './TypeScriptGraderConfiguration.js';
import type { Field } from './TypeScriptGraderConfiguration.js';
import { useCodeBundles } from './useCodeBundle.js';

interface SourceFile {
  id: string;
  path: string;
  source: string;
  asset: GraderCodeBundleAsset;
}

function isValidAsset(asset: GraderCodeBundleAsset): boolean {
  return Boolean(
    asset?.bundleId &&
    asset.mountPath &&
    !asset.mountPath.includes('\\') &&
    !asset.mountPath.includes('//') &&
    !asset.mountPath.split('/').includes('..'),
  );
}

/** Match returned file keys directly, or through an explicitly valid mount. */
function isModuleFile(
  modulePath: string,
  mountPath: string,
  filePath: string,
): boolean {
  if (
    !modulePath ||
    !mountPath ||
    !filePath ||
    [modulePath, mountPath, filePath].some(
      (path) =>
        path.includes('\\') ||
        path.includes('//') ||
        path.split('/').includes('..'),
    )
  ) {
    return false;
  }
  const mount = mountPath.replace(/\/+$/, '').replace(/^\.\//, '');
  const module = modulePath.replace(/^\.\//, '');
  const file = filePath.replace(/^\.\//, '');
  if (file === module) return true;
  if (module.startsWith('/') === file.startsWith('/')) {
    return !file.startsWith('/') && `${mount}/${file}` === module;
  }
  return module.startsWith('/')
    ? `${mount}/${file}` === module
    : `${mount}/${module}` === file;
}

export function TypeScriptCodeFiles({
  assets,
  modulePath,
  workspaceName,
  prompts,
  properties,
  evaluate,
}: {
  assets?: GraderCodeBundleAsset[];
  modulePath: string;
  workspaceName: string;
  prompts: Field[];
  properties: ReactNode;
  evaluate?: ReactNode;
}) {
  const styles = useStyles();
  const { recordAction } = useUserBi();
  const validAssets = (assets ?? []).filter(isValidAsset);
  const queries = useCodeBundles(workspaceName, validAssets);
  const [section, setSection] = useState('code');
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const files: SourceFile[] = queries.flatMap((query, index) => {
    const asset = validAssets[index];
    const data = query.data?.files;
    if (!asset || !data || typeof data !== 'object' || Array.isArray(data))
      return [];
    return Object.entries(data)
      .filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string',
      )
      .map(([path, source]) => ({
        id: `file:${index}:${path}`,
        path,
        source,
        asset,
      }));
  });
  const moduleFile = files.find(({ path, asset }) =>
    isModuleFile(modulePath, asset.mountPath, path),
  );
  const selectedFile =
    files.find(({ id }) => id === selectedFileId) ?? moduleFile ?? files[0];
  const loading = queries.some((query) => query.isPending);
  const failed = queries.some((query) => query.error);
  const missingModule = files.length > 0 && !moduleFile;

  return (
    <div className={styles.overviewTab}>
      <TabList
        aria-label="TypeScript grader views"
        className={styles.sourceTabs}
        selectedValue={section}
        onTabSelect={(_, data) => setSection(String(data.value))}
      >
        <Tab value="code">Code</Tab>
        {prompts.length > 0 && <Tab value="prompts">Prompts</Tab>}
        <Tab value="properties">Properties</Tab>
        {evaluate && <Tab value="evaluate">Evaluate</Tab>}
      </TabList>

      {section === 'code' && selectedFile && (
        <div className={styles.sourceLayout}>
          {files.length > 1 && (
            <nav className={styles.sourceNavigation} aria-label="Source files">
              {files.map(({ id, path }) => (
                <Button
                  key={id}
                  appearance={selectedFile.id === id ? 'subtle' : 'transparent'}
                  aria-current={selectedFile.id === id ? 'true' : undefined}
                  onClick={() => {
                    setSelectedFileId(id);
                    recordAction({
                      panelType: PanelType.GradersPage,
                      panelRegion: PanelRegion.Main,
                      actionScenario: ActionScenario.SelectGraderSourceFile,
                      actionScenarioType: ActionScenarioType.Open,
                    });
                  }}
                  title={path}
                >
                  {path}
                </Button>
              ))}
            </nav>
          )}
          <section
            className={`${styles.overviewCard} ${styles.sourceContent}`}
            aria-label={`Code bundle ${selectedFile.asset.bundleId}`}
          >
            {missingModule && (
              <p role="status">
                Module {modulePath || '(not specified)'} was not found in the
                attached bundles.
              </p>
            )}
            {failed && <p role="alert">Could not load all source bundles.</p>}
            <HighlightedSource
              path={selectedFile.path}
              source={selectedFile.source}
            />
            <p className={styles.readOnlySourcePath}>
              {selectedFile.path} · Bundle: {selectedFile.asset.bundleId} ·
              Mount: {selectedFile.asset.mountPath}
            </p>
          </section>
        </div>
      )}
      {section === 'code' && !selectedFile && (
        <section className={styles.overviewCard} aria-label="Code">
          {(assets?.length ?? 0) > validAssets.length && (
            <p role="alert">
              Code bundle reference has a missing ID or invalid mount path.
            </p>
          )}
          {loading ? (
            <p>Loading source files…</p>
          ) : failed ? (
            <p role="alert">Could not load source files for this bundle.</p>
          ) : validAssets.length === 0 ? (
            <p role="status">No code bundles are attached to this grader.</p>
          ) : (
            <p>No source files were returned for this bundle.</p>
          )}
        </section>
      )}
      {section === 'prompts' && prompts.length > 0 && (
        <section className={styles.overviewCard} aria-label="Prompts">
          <pre className={styles.readOnlyPrompt}>
            {prompts
              .map(({ path, value }) =>
                prompts.length === 1
                  ? display(value)
                  : `${label(path)}\n${display(value)}`,
              )
              .join('\n\n')}
          </pre>
        </section>
      )}
      {section === 'properties' && properties}
      {section === 'evaluate' && evaluate}
    </div>
  );
}
